import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaArrowRightLong } from 'react-icons/fa6';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authentication as teamsAuthentication } from '@microsoft/teams-js';

import { API_URL } from '../../services/api';
import { LAST_LOGIN_HINT_KEY } from '../../config/authStorageKeys';
import {
  ENABLE_LOGIN_GOOGLE,
  ENABLE_LOGIN_LINKEDIN,
  ENABLE_LOGIN_MICROSOFT,
  ENABLE_LOGIN_MICROSOFT_SSO,
  ENABLE_LOGIN_OTP,
  ENABLE_LOGIN_PASSWORD,
  ENABLE_SELF_SIGNUP,
} from '../../config/features';

// Reads the `email` claim straight out of the JWT payload, no signature check —
// used only to label the "Continue as X" shortcut, never for auth decisions.
function decodeJwtEmail(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    return JSON.parse(atob(padded)).email || null;
  } catch (_e) {
    return null;
  }
}

// KEEP THE REST OF YOUR IMPORTS AND THE COMPONENT STRUCTURE THE SAME
const Login = ({ onLogin, isTeamsInitialized }) => {
  // Added isTeamsInitialized prop
  const location = useLocation();
  const navigate = useNavigate();

  // NEW state to manage the two-step login flow
  const [loginStep, setLoginStep] = useState('identifier'); // 'identifier' -> 'password'

  // This state now manages the sub-flow *within* the 'password' step
  const [loginFlowState, setLoginFlowState] = useState(
    ENABLE_LOGIN_PASSWORD ? 'password' : 'otp_request',
  ); // 'password' or 'otp_request' or 'otp_verify'

  const [isNewUserFlow, setIsNewUserFlow] = useState(false); // ### ADDED: State to track if it's a new user OTP flow

  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    otp: '',
    emailForOtpDisplay: '',
    actualEmailForVerification: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);

  // Enterprise / Azure AD login state
  const [showEnterpriseLogin, setShowEnterpriseLogin] = useState(false);
  // tenant_id read from URL (?tenant_id=xxx) — set by enterprise admins on their custom login link.
  // Not exposed as a manual input; Microsoft auto-handles work vs personal accounts.
  const enterpriseTenantId =
    new URLSearchParams(location.search).get('tenant_id') || '';

  // Remembered identity from the last successful login — powers the one-click
  // "Continue as X" shortcut. method is 'microsoft_sso' (reuses the popup,
  // sidesteps the third-party-cookie blocking that breaks the silent iframe
  // check) or 'credential' (password/OTP accounts — re-sends a login OTP).
  const [lastLoginHint, setLastLoginHint] = useState(() => {
    try {
      const raw = localStorage.getItem(LAST_LOGIN_HINT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_e) {
      return null;
    }
  });

  const persistLoginHint = (hint) => {
    try {
      localStorage.setItem(LAST_LOGIN_HINT_KEY, JSON.stringify(hint));
    } catch (_e) {
      // localStorage unavailable (private browsing, quota) — shortcut just won't persist
    }
    setLastLoginHint(hint);
  };

  // Tags an in-flight popup login with which provider initiated it
  // ('google' | 'outlook' | 'microsoft_sso' | 'linkedin') so handleOauthMessage
  // knows which method to persist as the "last login" hint on success.
  const pendingOauthProviderRef = useRef(null);

  const oauthPopupRef = useRef(null);
  const popupPollerRef = useRef(null);

  // --- START: Existing useEffect for handling URL error parameter ---
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const errorParam = queryParams.get('error');

    if (errorParam) {
      const decodedError = decodeURIComponent(errorParam);
      toast.error(decodedError);
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('error');
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [location, navigate]);
  // --- END: Existing useEffect ---

  // --- START: Existing Callback for handling OAuth messages (for web flow) ---
  const handleOauthMessage = useCallback(
    (event) => {
      let expectedOrigin = '';
      try {
        expectedOrigin = new URL(API_URL).origin;
      } catch (e) {
        expectedOrigin = window.location.origin;
      }

      // Security: reject messages not from our backend origin
      if (event.origin !== expectedOrigin) return;

      if (event.data && typeof event.data === 'object') {
        const { success, token, user_id, project_token, error, provider } =
          event.data;
        if (typeof success === 'undefined' && !token && !error) {
          // More specific check
          return;
        }
        if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
          oauthPopupRef.current.close();
        }
        if (popupPollerRef.current) {
          clearInterval(popupPollerRef.current);
          popupPollerRef.current = null;
        }
        oauthPopupRef.current = null;
        setIsLoading(false);

        const oauthProvider = pendingOauthProviderRef.current;
        pendingOauthProviderRef.current = null;

        if (success) {
          toast.success(
            `Logged in successfully with ${provider || 'OAuth provider'}!`,
          );
          if (oauthProvider) {
            const email = decodeJwtEmail(token);
            if (email) {
              persistLoginHint({ email, method: oauthProvider });
            }
          }
          onLogin(token, user_id, project_token); // Assuming onLogin expects (token, userId)
        } else {
          toast.error(error || 'OAuth login failed. Please try again.');
        }
      }
    },
    // eslint-disable-next-line
    [onLogin, API_URL],
  );
  // --- END: Existing Callback for handling OAuth messages ---

  // --- START: Existing useEffect for setting up message listener (for web flow) ---
  useEffect(() => {
    // Only add this listener if not in Teams context or as a fallback
    // For this setup, we'll have initiateOAuthLogin decide based on context
    // So, this listener is primarily for the non-Teams web flow.
    window.addEventListener('message', handleOauthMessage);
    return () => {
      window.removeEventListener('message', handleOauthMessage);
      if (popupPollerRef.current) {
        clearInterval(popupPollerRef.current);
      }
      if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
        oauthPopupRef.current.close();
      }
    };
  }, [handleOauthMessage]);
  // --- END: Existing useEffect ---

  // --- START: Existing openOAuthPopup (for web flow) ---
  const openOAuthPopup = (authUrl) => {
    const width = 600,
      height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
      oauthPopupRef.current.close();
    }
    if (popupPollerRef.current) {
      clearInterval(popupPollerRef.current);
    }

    oauthPopupRef.current = window.open(
      authUrl,
      'oauthWindow',
      `toolbar=no,location=no,directories=no,status=no,menubar=no,
       scrollbars=yes,resizable=yes,copyhistory=no,width=${width},
       height=${height},top=${top},left=${left}`,
    );

    if (oauthPopupRef.current) {
      setIsLoading(true);
      popupPollerRef.current = setInterval(() => {
        if (oauthPopupRef.current && oauthPopupRef.current.closed) {
          clearInterval(popupPollerRef.current);
          popupPollerRef.current = null;
          if (isLoading) {
            toast.error(
              'Login process cancelled or popup closed unexpectedly.',
            );
            setIsLoading(false);
          }
          oauthPopupRef.current = null;
        } else if (!oauthPopupRef.current) {
          clearInterval(popupPollerRef.current);
          popupPollerRef.current = null;
        }
      }, 500);
    } else {
      toast.error(
        'Failed to open login window. Please check your popup blocker settings.',
      );
      setIsLoading(false);
    }
  };
  // --- END: Existing openOAuthPopup ---

  // --- START: REVISED initiateWebOAuthLogin (WEB FLOW ONLY) ---
  const initiateWebOAuthLogin = (providerBackendEndpointName) => {
    // 1. Open the popup window IMMEDIATELY to our new intermediate page.
    // This is a direct result of the user's click, so it won't be blocked.
    setIsLoading(true);

    try {
      // 2. Construct the URL for our intermediate page, passing the necessary
      // info (provider endpoint and API URL) as query parameters.
      const authStartPageUrl =
        `${window.location.origin}/web-auth-start.html` +
        `?provider_backend_endpoint_name=${providerBackendEndpointName}` +
        `&api_uri=${encodeURIComponent(API_URL)}`;

      // 3. Use the existing openOAuthPopup function. It already handles
      // popup window references, polling for closure, and other edge cases.
      openOAuthPopup(authStartPageUrl);
    } catch (err) {
      toast.error(
        err.message ||
          `Could not initiate Sign-In with ${providerBackendEndpointName}.`,
      );
      setIsLoading(false);
    }
  };

  // --- START: NEW initiateTeamsOAuthLogin (TEAMS FLOW ONLY) ---
  const initiateTeamsOAuthLogin = async (
    providerBackendEndpointName,
    providerShortName,
  ) => {
    if (!isTeamsInitialized) {
      toast.error('Teams environment not ready. Please try again.');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const finalSuccessRedirectInApp = window.location.origin + '/projects';
      const backendCallbackUri = `${API_URL}/oauth2callback`; // Ensure API_URL is defined
      const teamsStateObject = {
        original_frontend_url: finalSuccessRedirectInApp,
        app_name: providerShortName,
        isTeamsAuth: true,
      };
      const teamsCustomState = btoa(JSON.stringify(teamsStateObject));
      const authStartPageUrl =
        `${window.location.origin}/teams-auth-start.html` +
        `?provider_backend_endpoint_name=${providerBackendEndpointName}` +
        `&teams_custom_state=${encodeURIComponent(teamsCustomState)}` +
        `&backend_callback_uri=${encodeURIComponent(backendCallbackUri)}` +
        `&api_uri=${encodeURIComponent(API_URL)}`;

      // Use the Promise-based version of authenticate
      const result = await teamsAuthentication.authenticate({
        url: authStartPageUrl,
        width: 600,
        height: 700,
        isExternal: false, // Recommended for most tab scenarios
      });

      // Success: 'result' contains the string passed to notifySuccess()
      setIsLoading(false);
      if (result) {
        try {
          const authData = JSON.parse(result); // Expecting { token, user_id, provider }
          toast.success(
            `Logged in successfully with ${authData.provider || 'OAuth provider'} via Teams!`,
          );
          onLogin(authData.token, authData.user_id, authData.project_token);
        } catch (e) {
          toast.error('Failed to process login data from Teams.');
        }
      } else {
        // This case should be less common with the promise, as an empty result might still be a "success"
        // but an error during the flow would throw and be caught by the catch block.
        toast.error(
          'OAuth login via Teams completed, but no result data was returned.',
        );
      }
    } catch (error) {
      // This will catch errors from authenticate() or other issues
      setIsLoading(false);
      // The 'error' here could be a string (reason from notifyFailure) or an Error object
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      toast.error(`Teams OAuth failed: ${errorMessage || 'Unknown error'}`);
    }
  };

  // --- END: NEW initiateTeamsOAuthLogin ---
  // --- CORRECTED: OAuth button handlers ---
  // loginHint, when known (e.g. from the "Continue as X" shortcut), is passed
  // through to the provider so it pre-selects that account instead of
  // showing an account picker.
  const handleGoogleLogin = (loginHint) => {
    pendingOauthProviderRef.current = 'google';
    const query = loginHint
      ? `?login_hint=${encodeURIComponent(loginHint)}`
      : '';
    const endpoint = `redirect-url/google${query}`;
    if (isTeamsInitialized) {
      // Only rely on isTeamsInitialized
      initiateTeamsOAuthLogin(endpoint, 'google');
    } else {
      initiateWebOAuthLogin(endpoint);
    }
  };

  const handleMicrosoftLogin = (loginHint) => {
    pendingOauthProviderRef.current = 'outlook';
    const query = loginHint
      ? `?login_hint=${encodeURIComponent(loginHint)}`
      : '';
    const endpoint = `redirect-url/outlook${query}`;
    if (isTeamsInitialized) {
      // Only rely on isTeamsInitialized
      initiateTeamsOAuthLogin(endpoint, 'outlook');
    } else {
      initiateWebOAuthLogin(endpoint);
    }
  };

  const handleEnterpriseLogin = (loginHint) => {
    pendingOauthProviderRef.current = 'microsoft_sso';
    const tenantId = enterpriseTenantId.trim();
    const params = new URLSearchParams();
    if (tenantId) params.set('tenant_id', tenantId);
    if (loginHint) params.set('login_hint', loginHint);
    const query = params.toString();
    const endpoint = `sso/redirect-url/microsoft${query ? `?${query}` : ''}`;
    if (isTeamsInitialized) {
      initiateTeamsOAuthLogin(endpoint, 'microsoft_sso');
    } else {
      initiateWebOAuthLogin(endpoint);
    }
  };

  const handleLinkedInLogin = (loginHint) => {
    pendingOauthProviderRef.current = 'linkedin';
    const query = loginHint
      ? `?login_hint=${encodeURIComponent(loginHint)}`
      : '';
    const endpoint = `redirect-url/linkedin${query}`;
    if (isTeamsInitialized) {
      // Only rely on isTeamsInitialized
      initiateTeamsOAuthLogin(endpoint, 'linkedin');
    } else {
      initiateWebOAuthLogin(endpoint);
    }
  };
  // --- END: Modified OAuth button handlers ---

  // KEEP ALL YOUR OTHER HANDLERS (handleChange, password sign-in, OTP, etc.) AND JSX THE SAME
  // ... (formData, handleChange, startResendOtpTimer, useEffect for timer, handlePasswordSignIn, handleRequestLoginOtp, handleOtpSignIn, switchToOtpFlow, switchToPasswordFlow)
  // ... (currentFormSubmitHandler, mainButtonText logic)
  // ... (return statement with JSX)

  // Ensure the prop `isTeamsInitialized` is passed from App.js
  // Example of how it might be logged (optional):

  // Your existing password and OTP logic:
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const goBackToIdentifierStep = () => {
    setLoginStep('identifier');
    setLoginFlowState(ENABLE_LOGIN_PASSWORD ? 'password' : 'otp_request'); // Reset sub-flow to default
    setFormData((prev) => ({ ...prev, password: '', otp: '' }));
  };

  // --- NEW: Step 1 Handler ---
  const handleIdentifierCheck = async (e) => {
    e.preventDefault();
    const { identifier } = formData;
    if (!identifier) {
      toast.error('Please provide your Email.');
      return;
    }
    setIsLoading(true);
    try {
      // This endpoint needs to be created on your backend as discussed.
      const response = await fetch(`${API_URL}/api/auth/check-identifier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Error checking your details.');
      }

      if (data.user_exists) {
        // User exists, so proceed to the password step
        setLoginStep('password');
      } else {
        if (data.is_email) {
          if (ENABLE_SELF_SIGNUP || data.pre_approved) {
            // User does not exist, but signup is allowed (open signup or pre-approved)
            navigate('/sign-up', {
              state: {
                email: data.email,
                suggested_username: data.suggested_username,
                is_oauth_setting_password: data.is_oauth_setting_password,
              },
            });
          } else {
            // Signup disabled and not pre-approved — inform the user
            toast.error(
              'Account creation is disabled. Contact your administrator.',
            );
          }
        } else {
          // User does not exist and it's not an email -> show error
          toast.error('Invalid Email.');
        }
      }
    } catch (err) {
      toast.error(err.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const startResendOtpTimer = () => {
    setResendDisabled(true);
    setOtpTimer(60);
  };

  useEffect(() => {
    let intervalId = null;
    if (resendDisabled && otpTimer > 0) {
      intervalId = setInterval(() => {
        setOtpTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(intervalId);
            setResendDisabled(false);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [resendDisabled, otpTimer]);

  const handlePasswordSignIn = async (e) => {
    e.preventDefault();
    const { identifier, password } = formData;
    if (!identifier || !password) {
      toast.error('Please fill in your Email and Password.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        // Assuming /api/auth/login is correct
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password }),
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMsg = data.detail || data.message || 'Login failed.';
        throw new Error(errorMsg); // Throw an error object
      }
      toast.success(data.message || 'Logged in Successfully!');
      persistLoginHint({ email: identifier, method: 'credential' });
      // Assuming your onLogin expects (token, user_id) but backend sends {token, user_id, message}
      onLogin(data.token, data.user_id, data.project_token);
    } catch (err) {
      toast.error(
        err.message || 'Login failed. Please check your credentials.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestLoginOtp = async (e, identifierOverride) => {
    if (e) e.preventDefault();
    const identifier = identifierOverride || formData.identifier;
    if (!identifier) {
      toast.error('Please enter your Email to receive a code.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMsg =
          data.detail || data.message || 'Failed to send one-time code.';
        throw new Error(errorMsg);
      }
      //  ### ADDED: This is the key logic to determine the next step
      // The backend sends `user_exists: true` or `user_exists: false`.
      // We set our new state based on this. `isNewUserFlow` will be true if user_exists is false.
      setIsNewUserFlow(data.new_user || false);

      toast.success(
        data.message || 'One-time code sent to your registered email.',
      );
      setFormData((prev) => ({
        ...prev,
        emailForOtpDisplay: data.email_sent_to,
        actualEmailForVerification: data.actual_email_for_verification,
        otp: '',
      }));
      setLoginFlowState('otp_verify');
      startResendOtpTimer();
    } catch (err) {
      toast.error(
        err.message || 'Could not send one-time code. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSignIn = async (e) => {
    e.preventDefault();
    const { actualEmailForVerification, otp } = formData;
    if (!otp) {
      toast.error('Please enter the one-time code.');
      return;
    }
    if (!actualEmailForVerification) {
      toast.error(
        'Verification email not found. Please try requesting a code again.',
      );
      return;
    }
    setIsLoading(true);

    // ### ADDED: Conditional logic for endpoint and payload
    const endpoint = isNewUserFlow
      ? `${API_URL}/api/auth/login/verify-and-create-user` // Endpoint for new users
      : `${API_URL}/api/auth/login/verify-otp`; // Endpoint for existing users
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_for_verification: actualEmailForVerification,
          otp,
        }),
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMsg =
          data.detail || data.message || 'Sign in with code failed.';
        throw new Error(errorMsg);
      }
      toast.success(data.message || 'Logged in Successfully with code!');
      persistLoginHint({
        email: actualEmailForVerification,
        method: 'credential',
      });
      onLogin(data.token, data.user_id, data.project_token);
    } catch (err) {
      toast.error(
        err.message ||
          'Sign in with code failed. Please check the code or try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const switchToOtpFlow = () => {
    setLoginFlowState('otp_request');
    // ### ADDED: Reset the flow type when switching
    setIsNewUserFlow(false);
    setFormData((prev) => ({
      ...prev,
      password: '',
      otp: '',
    }));
  };

  const switchToPasswordFlow = () => {
    setLoginFlowState('password');
    // ### ADDED: Reset the flow type when switching back
    setIsNewUserFlow(false);
    setFormData((prev) => ({
      ...prev,
      otp: '',
    }));
  };

  // One-click shortcut for password/OTP accounts: skips straight to sending
  // a login OTP for the remembered email, landing the user on the code-entry
  // step. Passes the email directly to avoid racing the setFormData update.
  const handleContinueAsLastCredential = () => {
    if (!lastLoginHint || lastLoginHint.method !== 'credential') return;
    const email = lastLoginHint.email;
    setFormData((prev) => ({ ...prev, identifier: email }));
    setLoginStep('password');
    setLoginFlowState('otp_request');
    setIsNewUserFlow(false);
    handleRequestLoginOtp(null, email);
  };

  let currentFormSubmitHandler;
  let mainButtonText;

  if (loginStep === 'identifier') {
    currentFormSubmitHandler = handleIdentifierCheck;
    mainButtonText = 'Continue';
  } else {
    // loginStep === 'password'
    if (loginFlowState === 'password') {
      currentFormSubmitHandler = handlePasswordSignIn;
      mainButtonText = 'Sign in';
    } else if (loginFlowState === 'otp_request') {
      currentFormSubmitHandler = handleRequestLoginOtp;
      mainButtonText = 'Send Code';
    } else {
      // otp_verify
      currentFormSubmitHandler = handleOtpSignIn;
      mainButtonText = 'Sign In with Code';
    }
  }

  // Resolve which "Continue as X" shortcut to show, if any — based on the
  // method the last successful login used and whether it's still enabled.
  const microsoftIcon = (
    <svg
      className="h-4 w-4"
      viewBox="0 0 23 23"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path fill="#f35325" d="M1 1h10v10H1z" />
      <path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" />
      <path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  );
  let continueAsConfig = null;
  if (lastLoginHint?.method === 'credential' && ENABLE_LOGIN_OTP) {
    continueAsConfig = {
      onClick: handleContinueAsLastCredential,
      icon: (
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="m2 7 10 6 10-6" />
        </svg>
      ),
      caption: "We'll email you a one-time code",
    };
  } else if (
    lastLoginHint?.method === 'microsoft_sso' &&
    ENABLE_LOGIN_MICROSOFT_SSO
  ) {
    continueAsConfig = {
      onClick: () => handleEnterpriseLogin(lastLoginHint.email),
      icon: microsoftIcon,
    };
  } else if (lastLoginHint?.method === 'outlook' && ENABLE_LOGIN_MICROSOFT) {
    continueAsConfig = {
      onClick: () => handleMicrosoftLogin(lastLoginHint.email),
      icon: microsoftIcon,
    };
  } else if (lastLoginHint?.method === 'google' && ENABLE_LOGIN_GOOGLE) {
    continueAsConfig = {
      onClick: () => handleGoogleLogin(lastLoginHint.email),
      icon: <img src="/assets/google.svg" alt="Google" className="h-4 w-4" />,
    };
  } else if (lastLoginHint?.method === 'linkedin' && ENABLE_LOGIN_LINKEDIN) {
    continueAsConfig = {
      onClick: () => handleLinkedInLogin(lastLoginHint.email),
      icon: (
        <img
          src="/assets/icons8-linkedin.svg"
          alt="LinkedIn"
          className="h-4 w-4"
        />
      ),
    };
  }

  const theme = 'light'; // Later replace with context or store
  const isDark = theme === 'dark';

  return (
    <div
      className={`
      relative flex h-screen w-screen flex-col overflow-hidden md:flex-row
      ${isDark ? 'bg-black' : 'bg-radial-gradient'}
    `}
    >
      {/* DARK BG IMAGE */}
      {isDark && (
        <img
          src="/assets/auth-bg.svg"
          className="absolute inset-0 z-0 hidden h-full w-full object-cover lg:block"
          alt="Background"
        />
      )}

      {/* LEFT LOGIN */}
      <div className="relative z-10 flex w-full flex-col items-center justify-center overflow-y-auto p-5 lg:w-1/2">
        <div className="w-full max-w-md">
          {/* LOGO */}
          <div className="mb-5 flex items-center">
            <img
              alt="SuperManager AGI"
              src="/assets/super_agi_logo.png"
              className="h-16 w-16"
            />
            <h1
              className={`${isDark ? 'text-white' : 'text-black'} font-poppins ml-2 text-lg font-bold`}
            >
              SuperManager AGI
            </h1>
          </div>

          <h2
            className={`${isDark ? 'text-white' : 'text-black'} mb-6 text-2xl font-bold`}
          >
            Sign In
          </h2>

          {continueAsConfig && (
            <button
              type="button"
              onClick={continueAsConfig.onClick}
              disabled={isLoading}
              title={continueAsConfig.caption}
              className="mb-4 flex w-full items-center justify-center gap-2 overflow-hidden rounded-lg bg-[#6a65ef] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#5a54e0] disabled:opacity-50"
            >
              {continueAsConfig.icon}
              <span className="truncate">
                Continue as {lastLoginHint.email}
              </span>
            </button>
          )}

          <form onSubmit={currentFormSubmitHandler}>
            {/* IDENTIFIER */}
            <div className="relative mb-4">
              <label
                htmlFor="email"
                className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
              >
                Email
              </label>

              <input
                name="identifier"
                value={formData.identifier}
                onChange={handleChange}
                disabled={isLoading || loginStep === 'password'}
                placeholder="Enter your email"
                className={`
                w-full rounded-lg border px-3 py-3 pr-10 text-sm focus:outline-none
                ${
                  isDark
                    ? 'border-transparent bg-[#1E1F20] text-zinc-200 focus:border-[#6a65ef]'
                    : 'border-zinc-300 bg-white text-zinc-900 focus:border-[#6a65ef]'
                }
              `}
              />

              {loginStep === 'password' && (
                <button
                  type="button"
                  onClick={goBackToIdentifierStep}
                  className="absolute right-3 top-9 text-zinc-400 hover:text-white"
                >
                  ✎
                </button>
              )}
            </div>

            {/* PASSWORD + OTP SECTION */}
            {loginStep === 'password' && (
              <>
                {/* PASSWORD */}
                {loginFlowState === 'password' && (
                  <div className="mb-4">
                    <label
                      htmlFor="password"
                      className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
                    >
                      Password
                    </label>

                    <input
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className={`
                      w-full rounded-lg border px-3 py-3 text-sm focus:outline-none
                      ${
                        isDark
                          ? 'border-transparent bg-[#1E1F20] text-zinc-200 focus:border-[#6a65ef]'
                          : 'border-zinc-300 bg-white text-zinc-900 focus:border-[#6a65ef]'
                      }
                    `}
                    />
                  </div>
                )}

                {/* OTP VERIFY */}
                {loginFlowState === 'otp_verify' && (
                  <div className="mb-4">
                    <label
                      className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
                    >
                      One-time code sent to{' '}
                      {formData.emailForOtpDisplay || 'your email'}
                    </label>

                    <input
                      name="otp"
                      value={formData.otp}
                      onChange={handleChange}
                      maxLength={6}
                      placeholder="Enter your 6-Digit OTP"
                      className={`
                      w-full rounded-lg border px-3 py-3 text-sm focus:outline-none
                      ${
                        isDark
                          ? 'border-transparent bg-[#1E1F20] text-zinc-200 focus:border-[#6a65ef]'
                          : 'border-zinc-300 bg-white text-zinc-900 focus:border-[#6a65ef]'
                      }
                    `}
                    />

                    {/* RESEND OTP */}
                    <button
                      type="button"
                      onClick={handleRequestLoginOtp}
                      disabled={resendDisabled || isLoading}
                      className={`mt-2 text-xs ${
                        resendDisabled
                          ? 'text-zinc-500'
                          : isDark
                            ? 'text-white underline'
                            : 'text-[#6a65ef] underline'
                      }`}
                    >
                      {resendDisabled
                        ? `Resend code in ${otpTimer}s`
                        : 'Resend code'}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* MAIN BUTTON */}
            <div className="flex flex-col items-start gap-4">
              <button
                type="submit"
                disabled={isLoading}
                className="flex w-auto items-center gap-4 rounded-3xl border bg-white p-[1px]"
              >
                <div className="font-poppins flex h-9 items-center justify-center px-6 font-medium text-black">
                  {isLoading
                    ? loginFlowState === 'otp_request'
                      ? 'Sending...'
                      : loginFlowState === 'otp_verify'
                        ? 'Verifying...'
                        : 'Signing in...'
                    : mainButtonText}
                </div>

                {!isLoading && (
                  <span className="mr-[1px] flex h-9 w-9 items-center justify-center rounded-full bg-[#6a65ef] text-lg text-white">
                    <FaArrowRightLong />
                  </span>
                )}
              </button>

              {/* SWITCH PASSWORD / OTP */}
              {loginStep === 'password' && (
                <div className="flex items-center justify-center gap-6">
                  {ENABLE_LOGIN_OTP && loginFlowState === 'password' && (
                    <button
                      type="button"
                      onClick={switchToOtpFlow}
                      className="text-sm text-[#6a65ef] hover:underline"
                    >
                      Sign in with one-time code
                    </button>
                  )}

                  {ENABLE_LOGIN_PASSWORD && loginFlowState !== 'password' && (
                    <button
                      type="button"
                      onClick={switchToPasswordFlow}
                      className="text-sm text-[#6a65ef] hover:underline"
                    >
                      Sign in with password
                    </button>
                  )}
                </div>
              )}
            </div>
          </form>

          {(ENABLE_LOGIN_GOOGLE ||
            ENABLE_LOGIN_MICROSOFT ||
            ENABLE_LOGIN_LINKEDIN) && (
            <>
              {/* OR */}
              <div className="my-6 flex items-center">
                <hr className="w-full border-zinc-600" />
                <span className="px-2 text-sm text-zinc-400">OR</span>
                <hr className="w-full border-zinc-600" />
              </div>

              {/* OAUTH */}
              <div className="flex justify-center gap-6">
                {ENABLE_LOGIN_GOOGLE && (
                  <button
                    onClick={() => handleGoogleLogin()}
                    className="h-11 w-11 rounded-full bg-white p-1"
                  >
                    <img
                      alt="Google"
                      src="/assets/google.svg"
                      className="h-full"
                    />
                  </button>
                )}

                {ENABLE_LOGIN_MICROSOFT && (
                  <button
                    onClick={() => handleMicrosoftLogin()}
                    className="h-11 w-11 rounded-full bg-white p-1"
                  >
                    <img
                      alt="Microsoft"
                      src="/assets/icons8-microsoft-outlook.svg"
                      className="h-full"
                    />
                  </button>
                )}

                {ENABLE_LOGIN_LINKEDIN && (
                  <button
                    onClick={() => handleLinkedInLogin()}
                    className="h-11 w-11 rounded-full"
                    style={{ background: '#0288d1' }}
                  >
                    <img
                      alt="LinkedIn"
                      src="/assets/icons8-linkedin.svg"
                      className="h-full"
                    />
                  </button>
                )}
              </div>
            </>
          )}

          {/* TERMS */}
          <p className="mt-8 flex justify-center gap-4 text-sm text-gray-400">
            <a
              href="https://www.supermanager.co/terms-of-service"
              className="underline"
            >
              Terms of Service
            </a>
            <a
              href="https://www.supermanager.co/privacy-policy"
              className="underline"
            >
              Privacy Policy
            </a>
          </p>

          {/* ENTERPRISE / AZURE AD SSO */}
          {ENABLE_LOGIN_MICROSOFT_SSO && (
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowEnterpriseLogin((v) => !v)}
                className={`w-full text-sm ${
                  isDark
                    ? 'text-zinc-400 hover:text-white'
                    : 'text-zinc-500 hover:text-zinc-800'
                } flex items-center justify-center gap-2 transition-colors`}
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="2" y="7" width="20" height="14" rx="2" />
                  <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                </svg>
                Enterprise / Azure AD login
                <svg
                  className={`h-3 w-3 transition-transform ${
                    showEnterpriseLogin ? 'rotate-180' : ''
                  }`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>

              {showEnterpriseLogin && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => handleEnterpriseLogin()}
                    disabled={isLoading}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#6a65ef] px-4 py-2 text-sm font-medium text-[#6a65ef] transition-colors hover:bg-[#6a65ef] hover:text-white disabled:opacity-50"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 23 23"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path fill="#f35325" d="M1 1h10v10H1z" />
                      <path fill="#81bc06" d="M12 1h10v10H12z" />
                      <path fill="#05a6f0" d="M1 12h10v10H1z" />
                      <path fill="#ffba08" d="M12 12h10v10H12z" />
                    </svg>
                    Continue with Microsoft
                    {enterpriseTenantId && (
                      <span className="rounded bg-[#6a65ef]/10 px-1 py-0.5 text-xs font-normal">
                        tenant locked
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT PANEL LIGHT ONLY */}
      {!isDark && (
        <div className="hidden w-1/2 items-center justify-center p-16 lg:flex">
          <div className="max-w-xl">
            <h1 className="mb-6 text-5xl font-semibold text-zinc-900">
              See autonomous project management running on your real projects.
            </h1>

            <p className="mb-8 text-lg text-zinc-600">
              Experience autonomous project management in action.
            </p>

            <p className="mb-8 text-sm text-zinc-500">
              We may request additional information to verify your identity.
            </p>

            <div className="flex gap-6 text-sm text-zinc-700">
              <span className="cursor-pointer underline">Documentation</span>
              <span className="cursor-pointer underline">FAQ</span>
              <span className="cursor-pointer underline">Resource Hub</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
